import { 
    Box, 
    Container, 
    Typography, 
    TextField, 
    InputAdornment,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Button,
    Stack,
    CircularProgress,
    Pagination
} from "@mui/material";
import patternTop from "../assets/patternTop.png";
import patternBottom from "../assets/patternBottom.png";
import { HeadTags } from "../components/headTags";
import { useState, useEffect } from "react";
import SearchIcon from '@mui/icons-material/Search';
import { api } from "../lib/api";

const DatabaseKader = () => {
    const [search, setSearch] = useState("");
    const [loading, setLoading] = useState(false);
    const [kaderList, setKaderList] = useState([]);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);

    const getKaderData = async (searchQuery = "", pageNum = 1) => {
        try {
            setLoading(true);
            const res = await api.get("/kader", {
                params: { 
                    search: searchQuery,
                    page: pageNum
                }
            });
            
            // Menangani response paginasi dari Laravel
            // Biasanya format: { data: [...], current_page: 1, last_page: 10, ... }
            // Atau jika dibungkus resource: { data: [...], meta: { last_page: 10, ... } }
            
            const responseData = res.data;
            console.log("API Response:", responseData); // Debugging

            // Case 1: Nested Pagination (e.g. { data: { data: [...], last_page: 5 } })
            if (responseData.data && responseData.data.data && Array.isArray(responseData.data.data)) {
                setKaderList(responseData.data.data);
                setTotalPages(responseData.data.last_page || 1);
            }
            // Case 2: Direct Pagination (e.g. { data: [...], last_page: 5 })
            else if (responseData.data && Array.isArray(responseData.data)) {
                setKaderList(responseData.data);
                setTotalPages(responseData.last_page || responseData.meta?.last_page || 1);
            }
            // Fallback
            else {
                setKaderList([]);
            }
            
            setLoading(false);
        } catch (error) {
            console.error("Failed to fetch kader data:", error);
            setLoading(false);
        }
    };

    useEffect(() => {
        const timeoutId = setTimeout(() => {
            getKaderData(search, page);
        }, 500);
        return () => clearTimeout(timeoutId);
    }, [search, page]);

    const handleSearch = (event) => {
        setSearch(event.target.value);
        setPage(1); // Reset ke halaman 1 saat mencari
    };

    const handlePageChange = (event, value) => {
        setPage(value);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    return (
        <>
            <HeadTags title="Database Kader" />
            {/* Hero Section */}
            <Box
                bgcolor="primary.main"
                height={300}
                width="100%"
                display="flex"
                justifyContent="center"
                alignItems="center"
                position="relative"
                overflow="hidden"
            >
                <Box
                    component="img"
                    src={patternTop}
                    position="absolute"
                    left={{ xs: -200, md: -100 }}
                    top={0}
                    height="100%"
                />
                <Box
                    component="img"
                    src={patternBottom}
                    position="absolute"
                    right={{ xs: -200, md: -100 }}
                    bottom={0}
                    height="100%"
                />
                <Typography
                    variant="h3"
                    color="white"
                    fontWeight="bold"
                    textAlign="center"
                    zIndex={1}
                >
                    Database Kader
                </Typography>
            </Box>

            <Container sx={{ py: 5, minHeight: '60vh' }}>
                {/* Search and Filter Section */}
                <Stack 
                    direction={{ xs: 'column', sm: 'row' }} 
                    spacing={2} 
                    mb={4} 
                    justifyContent="space-between"
                    alignItems="center"
                >
                    <Typography variant="h5" fontWeight="bold" color="primary">
                        Daftar Kader
                    </Typography>
                    <Box display="flex" gap={2}>
                        <TextField
                            placeholder="Cari nama, angkatan..."
                            variant="outlined"
                            size="small"
                            value={search}
                            onChange={handleSearch}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon color="action" />
                                    </InputAdornment>
                                ),
                            }}
                            sx={{ width: { xs: '100%', sm: 300 } }}
                        />
                        <Button variant="contained" color="primary" onClick={() => getKaderData(search, 1)}>
                            Filter
                        </Button>
                    </Box>
                </Stack>

                {/* Table Section */}
                <TableContainer component={Paper} elevation={3}>
                    <Table sx={{ minWidth: 650 }} aria-label="simple table">
                        <TableHead sx={{ bgcolor: 'primary.main' }}>
                            <TableRow>
                                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>No</TableCell>
                                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Nama</TableCell>
                                {/* <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Alamat</TableCell> */}
                                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Angkatan</TableCell>
                                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Keterangan</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading ? (
                                <TableRow>
                                    <TableCell colSpan={5} align="center" sx={{ py: 5 }}>
                                        <CircularProgress />
                                        <Typography variant="body2" sx={{ mt: 2 }}>Memuat data...</Typography>
                                    </TableCell>
                                </TableRow>
                            ) : kaderList.length > 0 ? (
                                kaderList.map((row, index) => (
                                    <TableRow
                                        key={row.id}
                                        sx={{ '&:last-child td, &:last-child th': { border: 0 }, '&:hover': { bgcolor: 'action.hover' } }}
                                    >
                                        <TableCell component="th" scope="row">
                                            {index + 1}
                                        </TableCell>
                                        <TableCell>{row.name}</TableCell>
                                        {/* <TableCell>{row.address ? row.address : "-"}</TableCell> */}
                                        <TableCell>{row.batch}</TableCell>
                                        <TableCell>{row.description}</TableCell>
                                    </TableRow>
                                ))
                            ) : (
                                <TableRow>
                                    <TableCell colSpan={5} align="center" sx={{ py: 3 }}>
                                        <Typography variant="body1" color="text.secondary">
                                            Data tidak ditemukan
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>

                {/* Pagination Section */}
                {totalPages > 1 && (
                    <Box display="flex" justifyContent="center" mt={4}>
                        <Pagination 
                            count={totalPages} 
                            page={page} 
                            onChange={handlePageChange} 
                            color="primary" 
                            showFirstButton 
                            showLastButton
                            shape="rounded"
                        />
                    </Box>
                )}
            </Container>
        </>
    );
};

export default DatabaseKader;
